import React from "react";
import { useTranslation } from "react-i18next";
import { Maturity } from "../../../../types";
import { YesNoRadioButtons } from "../../../common/radio-buttons/radio-buttons";
import FullPicker from "../../../common/picker/full-picker";
import { FeatureGate } from "../../../common/feature-gate/FeatureGate";
import { ModeFlags } from "../../../../hooks/game-settings/useGameSettings";

interface BasicSettings {
  quickStartGuide: boolean;
  writeDuration: number;
  maturity: Maturity;
  moderation: boolean;
}

interface BasicGameSettingsProps {
  settings: BasicSettings;
  onChange: (settings: Partial<BasicSettings>) => void;
  modeFlags: ModeFlags;
  canUseMature: boolean;
}

const BasicGameSettings: React.FC<BasicGameSettingsProps> = ({
  settings,
  onChange,
  modeFlags,
  canUseMature,
}) => {
  const { t } = useTranslation(["game-settings", "common"]);

  const isSolo = !!modeFlags.solo_writing;

  const handleMaturity = (value: boolean) => {
    onChange({ maturity: value ? Maturity.ALL_AGES : Maturity.MATURE });
  };

  return (
    <>
      <YesNoRadioButtons
        label={t("body.quickStart")}
        subLabel={t("body.quickStart2")}
        value={settings.quickStartGuide}
        onSelect={(value) => onChange({ quickStartGuide: value })}
      />

      <FullPicker
        label={t("body.timeToWrite")}
        subLabel={t("body.timeToWrite2")}
        value={settings.writeDuration}
        onSelect={(value) => onChange({ writeDuration: value })}
        options={[
          {
            value: 10,
            labels: [t("speeds.sonic", { ns: "common" }), "10 sec"],
          },
          {
            value: 60,
            labels: [t("speeds.lightning", { ns: "common" }), "1 min"],
          },
          {
            value: 180,
            labels: [t("speeds.frantic", { ns: "common" }), "3 min"],
          },
          {
            value: 300,
            labels: [t("speeds.brisk", { ns: "common" }), "5 min"],
          },
          {
            value: 600,
            labels: [t("speeds.leisurely", { ns: "common" }), "10 min"],
          },
          { value: 180, isCustom: true, customLabel: "CUSTOM (seconds)" },
        ]}
      />

      {canUseMature && !isSolo && (
        <YesNoRadioButtons
          label={t("body.maturity")}
          subLabel={isSolo ? t("body.maturity2Solo") : undefined}
          value={settings.maturity === Maturity.ALL_AGES}
          yesLabel={t("label.allAges", { ns: "common" })}
          noLabel={t("label.mature", { ns: "common" })}
          onSelect={handleMaturity}
        />
      )}

      {!isSolo && (
        <YesNoRadioButtons
          label={t("body.moderation")}
          subLabel={t("body.moderation2")}
          value={settings.moderation}
          radioSubheader=""
          onSelect={(value) => onChange({ moderation: value })}
        />
      )}
    </>
  );
};

export default BasicGameSettings;
export type { BasicSettings, };
